import { useRef, useState } from "react";
import type { PopupAd, PopupAdMediaType } from "@/types";
import { uploadPopupAdMedia, type PopupAdInput } from "@/services/adminApi";
import { POPUP_PAGE_OPTIONS } from "@/config/popupPages";
import { toInputDateTime } from "@/pages/admin/popup-ads/utils";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500";

interface PopupAdEditorProps {
  initial: PopupAd | null;
  token: string | null;
  saving: boolean;
  onSave: (input: PopupAdInput) => void;
  onCancel: () => void;
}

/** Create/edit form for a popup ad — media, targeting, frequency and schedule. */
export default function PopupAdEditor({ initial, token, saving, onSave, onCancel }: PopupAdEditorProps) {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [mediaType, setMediaType] = useState<PopupAdMediaType>(initial?.mediaType ?? "image");
  const [mediaUrl, setMediaUrl] = useState(initial?.mediaUrl ?? "");
  const [linkUrl, setLinkUrl] = useState(initial?.linkUrl ?? "");
  const [linkLabel, setLinkLabel] = useState(initial?.linkLabel ?? "");
  const [targetPages, setTargetPages] = useState<string[]>(initial?.targetPages ?? ["all"]);
  const [frequencyCap, setFrequencyCap] = useState(String(initial?.frequencyCap ?? 1));
  const [delaySeconds, setDelaySeconds] = useState(String(initial?.delaySeconds ?? 1));
  const [startAt, setStartAt] = useState(toInputDateTime(initial?.startAt ?? ""));
  const [endAt, setEndAt] = useState(toInputDateTime(initial?.endAt ?? ""));
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [priority, setPriority] = useState(String(initial?.priority ?? 0));
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);

  const togglePage = (value: string) => {
    setTargetPages((prev) => {
      if (value === "all") return ["all"];
      const withoutAll = prev.filter((p) => p !== "all");
      const next = withoutAll.includes(value)
        ? withoutAll.filter((p) => p !== value)
        : [...withoutAll, value];
      return next.length ? next : ["all"];
    });
  };

  const handleFile = async (file?: File) => {
    if (!file) return;
    setError("");
    try {
      setUploading(true);
      const result = await uploadPopupAdMedia(file, token);
      setMediaUrl(result.url);
      setMediaType(result.mediaType);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed. Please try another file.");
    } finally {
      setUploading(false);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError("Please enter a title for the popup.");
      return;
    }
    if (!mediaUrl.trim()) {
      setError("Please upload an image / video or paste a media URL.");
      return;
    }
    setError("");
    onSave({
      id: initial?.id,
      title: title.trim(),
      mediaType,
      mediaUrl: mediaUrl.trim(),
      linkUrl: linkUrl.trim(),
      linkLabel: linkLabel.trim(),
      targetPages,
      frequencyCap: Math.max(0, Number(frequencyCap) || 0),
      delaySeconds: Math.max(0, Number(delaySeconds) || 0),
      startAt,
      endAt,
      isActive,
      priority: Math.max(0, Number(priority) || 0),
    });
  };

  return (
    <form onSubmit={submit} className="rounded-lg border border-primary-300 bg-background-50 p-5">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-base font-bold text-foreground-950">
          {initial ? "Edit popup ad" : "New popup ad"}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold text-foreground-600 hover:bg-background-100 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-close-line"></i> Close
        </button>
      </div>

      {error && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 p-3 text-sm text-secondary-900">
          <i className="ri-error-warning-line mt-0.5"></i>
          <span>{error}</span>
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Media */}
        <div>
          <label className={labelClass}>Media (image or short video)</label>
          <div className="mb-2 flex h-40 items-center justify-center overflow-hidden rounded-md border border-dashed border-background-300 bg-background-100/60">
            {mediaUrl ? (
              mediaType === "video" ? (
                <video src={mediaUrl} className="h-full w-full object-contain" controls muted />
              ) : (
                <img src={mediaUrl} alt="Popup preview" className="h-full w-full object-cover object-top" />
              )
            ) : (
              <span className="text-xs text-foreground-400">No media yet — upload or paste a URL</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif,image/avif,video/mp4,video/webm,video/ogg,video/quicktime"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm font-semibold text-foreground-800 hover:border-primary-400 disabled:opacity-60 whitespace-nowrap cursor-pointer"
            >
              <i className={uploading ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"}></i>
              {uploading ? "Uploading…" : "Upload file"}
            </button>
            <select
              value={mediaType}
              onChange={(e) => setMediaType(e.target.value as PopupAdMediaType)}
              className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
            >
              <option value="image">Image</option>
              <option value="video">Video</option>
            </select>
          </div>
          <input
            value={mediaUrl}
            onChange={(e) => setMediaUrl(e.target.value)}
            placeholder="…or paste an image / video URL"
            className={`${inputClass} mt-2`}
          />
        </div>

        {/* Details */}
        <div className="space-y-4">
          <div>
            <label htmlFor="popup-title" className={labelClass}>Title</label>
            <input
              id="popup-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Grow your business with a featured listing"
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="popup-link" className={labelClass}>Click-through link (optional)</label>
              <input
                id="popup-link"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="/listing-plans or https://…"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="popup-link-label" className={labelClass}>Button label (optional)</label>
              <input
                id="popup-link-label"
                value={linkLabel}
                onChange={(e) => setLinkLabel(e.target.value)}
                placeholder="e.g. See plans"
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div>
              <label htmlFor="popup-freq" className={labelClass}>Times per visitor</label>
              <input
                id="popup-freq"
                type="number"
                min={0}
                max={1000}
                value={frequencyCap}
                onChange={(e) => setFrequencyCap(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="popup-delay" className={labelClass}>Delay (sec)</label>
              <input
                id="popup-delay"
                type="number"
                min={0}
                max={120}
                value={delaySeconds}
                onChange={(e) => setDelaySeconds(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="popup-priority" className={labelClass}>Priority</label>
              <input
                id="popup-priority"
                type="number"
                min={0}
                max={999}
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
          <p className="text-xs text-foreground-500">
            Set “Times per visitor” to 0 for unlimited. When several popups match a page, the highest
            priority wins.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="popup-start" className={labelClass}>Start date (optional)</label>
              <input
                id="popup-start"
                type="datetime-local"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
                className={`${inputClass} cursor-pointer`}
              />
            </div>
            <div>
              <label htmlFor="popup-end" className={labelClass}>End date (optional)</label>
              <input
                id="popup-end"
                type="datetime-local"
                value={endAt}
                onChange={(e) => setEndAt(e.target.value)}
                className={`${inputClass} cursor-pointer`}
              />
            </div>
          </div>
          <p className="text-xs text-foreground-500">
            Leave the end date blank to run indefinitely, or set one to auto-stop the ad on a date.
          </p>
        </div>
      </div>

      {/* Targeting */}
      <div className="mt-5 border-t border-background-100 pt-4">
        <label className={labelClass}>Show on which pages</label>
        <div className="flex flex-wrap gap-2">
          {POPUP_PAGE_OPTIONS.map((opt) => {
            const checked = targetPages.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => togglePage(opt.value)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  checked
                    ? "border-primary-400 bg-primary-100 text-primary-800"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                }`}
              >
                <i className={checked ? "ri-checkbox-circle-fill" : "ri-checkbox-blank-circle-line"}></i>
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-background-100 pt-4">
        <label className="flex cursor-pointer items-center gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={isActive}
            onClick={() => setIsActive((v) => !v)}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer ${
              isActive ? "bg-primary-500" : "bg-background-300"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-background-50 transition-transform ${
                isActive ? "translate-x-5" : "translate-x-0.5"
              }`}
            ></span>
          </button>
          <span className="text-sm font-semibold text-foreground-900">
            {isActive ? "Enabled — live when in the date window" : "Disabled — never shown"}
          </span>
        </label>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 hover:border-primary-400 whitespace-nowrap cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
            {initial ? "Save popup" : "Create popup"}
          </button>
        </div>
      </div>
    </form>
  );
}